import React, { useEffect, useRef } from "react";

function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export default function MessageBubble({ message, highlightText = "", isActive = false }) {
  const ref = useRef(null);

  // When this message becomes active, scroll it into view and briefly pulse
  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
      ref.current.animate(
        [
          { boxShadow: "0 0 0 rgba(59,130,246,0)" },
          { boxShadow: "0 0 12px rgba(59,130,246,0.4)" },
          { boxShadow: "0 0 0 rgba(59,130,246,0)" },
        ],
        { duration: 700 }
      );
    }
  }, [isActive]);

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
  const renderHighlighted = (text = "", query = "") => {
    if (!query) return text;
    const q = query.toString();
    const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, "i"));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} className={`bg-yellow-200 rounded px-0.5`}>{part}</mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  if (!message) return null;

  const isOutbound = message.direction === "outbound";

  return (
    <div className={`flex items-start gap-2 ${isOutbound ? "flex-row-reverse" : ""}`}>
      {/* Avatar */}
      <div
        className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold ${
          isOutbound
            ? "bg-gradient-to-br from-gray-700 to-gray-900"
            : "bg-gradient-to-br from-gray-400 to-gray-500"
        }`}
      >
        {isOutbound ? "Me" : message.phone_number?.slice(-2) || "??"}
      </div>

      {/* Message Bubble */}
      <div ref={ref} className={`max-w-md ${isOutbound ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-4 py-3 rounded-2xl shadow-sm ${
            isOutbound
              ? "bg-gray-900 text-white rounded-br-none"
              : "bg-white text-gray-800 rounded-bl-none border border-gray-200"
          } ${isActive ? "ring-2 ring-blue-300" : ""}`}
        >
          {/* PDF/Document indicator */}
          {message.type === "document" && message.media_url && (
            <div
              className={`mb-2 p-3 rounded-xl flex items-center gap-3 ${
                isOutbound ? "bg-white/10" : "bg-gray-50"
              }`}
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold text-xs">
                  {message.metadata?.type === "invoice"
                    ? "📄 Invoice"
                    : message.metadata?.type === "quotation"
                    ? "📋 Quotation"
                    : "📎 Document"}
                </p>
                <p className="text-xs opacity-75">
                  {message.metadata?.invoiceNumber ||
                    message.metadata?.quotationNumber ||
                    "Document"}
                </p>
              </div>
              <a
                href={message.media_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                  isOutbound
                    ? "bg-white text-gray-900 hover:bg-gray-100"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
              >
                View
              </a>
            </div>
          )}

          {/* Image */}
          {message.type === "image" && message.media_url && (
            <img
              src={message.media_url}
              alt="Shared media"
              className="max-w-full rounded-lg mb-2"
            />
          )}

          {/* Message Text */}
          <p className="break-words whitespace-pre-line text-sm leading-relaxed">
            {renderHighlighted(message.message_text || "", highlightText)}
          </p>
        </div>

        {/* Timestamp and Status */}
        <div
          className={`flex items-center gap-1 mt-1 text-xs text-gray-500 ${
            isOutbound ? "flex-row-reverse" : ""
          }`}
        >
          <span>{formatTime(message.timestamp)}</span>
          {isOutbound && (
            <span className={message.status === "read" ? "text-[#53BDEB]" : "text-gray-900"}>
              {message.status === "sent" && "✓"}
              {message.status === "delivered" && "✓✓"}
              {message.status === "read" && "✓✓"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}